import { useGetTicketTypesByEventId } from '@sunrise-ui/api-client';
import {
  Button,
  ButtonVariants,
  isNative,
  Loader,
  TextReg,
  useCheckoutModalStore,
  useDynamicStyles,
  useMediaQueries,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useOrderCalculations } from '../hooks/useOrderCalculations';
import { useCheckoutFormStore } from '../stores/useCheckoutFormStore';
import { DiscountCodeInput } from './DiscountCodeInput';
import { OrderSummary } from './OrderSummary';
import { TicketCard } from './TicketCard';

type Props = {
  setIsTwoSteps: (isTwoSteps: boolean) => void;
};

export function TicketSelectionStep({ setIsTwoSteps }: Props) {
  const styles = useDynamicStyles(createStyles);
  const {
    updateTicketQuantities,
    appliedPromo,
    setAppliedPromo,
    ticketQuantities: prefilledTicketQuantities,
  } = useCheckoutFormStore();
  const { eventId, initialTicketTypeId, setCurrentStep } =
    useCheckoutModalStore();
  const { isMobile } = useMediaQueries();

  const [ticketQuantities, setTicketQuantities] = useState<Map<string, number>>(
    prefilledTicketQuantities,
  );
  const hasInitialised = useRef(prefilledTicketQuantities.size > 0);

  const { data: onSaleTicketTypes, isLoading: isLoadingSaleTickets } =
    useGetTicketTypesByEventId({
      eventId,
      isOnSale: true,
    });
  const { data: notOnSaleTicketTypes, isLoading: isLoadingNotOnSaleTickets } =
    useGetTicketTypesByEventId({
      eventId,
      isOnSale: false,
    });

  // Sort ticket types by price (lowest to highest)
  const sortedTicketTypes = useMemo(() => {
    if (!onSaleTicketTypes || !notOnSaleTicketTypes) return [];

    const sortedSaleTypes = [...onSaleTicketTypes]
      .filter((type) => type.id)
      .sort((a, b) => a.price - b.price);
    const sortedNotOnSaleTypes = [...notOnSaleTicketTypes]
      .filter((type) => type.id)
      .sort((a, b) => a.price - b.price);
    return [...sortedSaleTypes, ...sortedNotOnSaleTypes];
  }, [onSaleTicketTypes, notOnSaleTicketTypes]);

  // Initialize quantities when ticket types load (only if not already prefilled from store)
  useEffect(() => {
    // Only initialize with defaults if there are no prefilled quantities
    if (
      onSaleTicketTypes &&
      onSaleTicketTypes.length > 0 &&
      !hasInitialised.current
    ) {
      hasInitialised.current = true;
      const initialQuantities = new Map<string, number>();

      // If initialTicketTypeId is provided, try to select that ticket
      if (initialTicketTypeId) {
        const targetTicket = onSaleTicketTypes.find(
          (type) => type.id === initialTicketTypeId,
        );
        if (targetTicket) {
          initialQuantities.set(initialTicketTypeId, 1);
        }
      }

      // Fallback: select first active ticket if no initial ticket or not found
      if (initialQuantities.size === 0) {
        initialQuantities.set(onSaleTicketTypes[0].id, 1);
      }

      setTicketQuantities(initialQuantities);
    }
  }, [onSaleTicketTypes, initialTicketTypeId]);

  // Calculate total quantity of tickets selected
  const totalQuantity = Array.from(ticketQuantities.values()).reduce(
    (sum, qty) => sum + qty,
    0,
  );

  // Handlers for increasing/decreasing ticket quantities
  const handleIncrease = (ticketTypeId: string) => () => {
    setTicketQuantities((prev) => {
      const newMap = new Map(prev);
      newMap.set(ticketTypeId, (prev.get(ticketTypeId) || 0) + 1);
      return newMap;
    });
  };

  const handleDecrease = (ticketTypeId: string) => () => {
    setTicketQuantities((prev) => {
      const newMap = new Map(prev);
      const prevQty = prev.get(ticketTypeId) || 0;
      if (prevQty <= 1) {
        newMap.delete(ticketTypeId);
      } else {
        newMap.set(ticketTypeId, prevQty - 1);
      }
      return newMap;
    });
  };

  // Handle clearing discount
  const handleClearDiscount = () => {
    setAppliedPromo(undefined);
  };

  // Handle next button click
  const handleNext = () => {
    // Validate that at least one ticket is selected (button should be disabled anyway)
    if (totalQuantity === 0) {
      return;
    }

    // Save ticket quantities to store (appliedPromo already in store)
    updateTicketQuantities(ticketQuantities);

    // Navigate to next step
    setCurrentStep('attendeeDetails');
  };

  const { data } = useOrderCalculations({
    ticketQuantities,
    appliedPromo,
  });

  useEffect(() => {
    setIsTwoSteps(isNative || data?.total === 0);
  }, [data?.total]);

  // Show loading state
  if (isLoadingSaleTickets || isLoadingNotOnSaleTickets) {
    return (
      <View style={styles.loadingContainer}>
        <Loader />
      </View>
    );
  }

  // Show empty state
  if (!onSaleTicketTypes || onSaleTicketTypes.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <TextReg>No tickets available for this event.</TextReg>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.column}>
          {sortedTicketTypes.map((ticketType) => (
            <TicketCard
              key={ticketType.id}
              ticketType={ticketType}
              quantity={ticketQuantities.get(ticketType.id) || 0}
              onIncrease={handleIncrease(ticketType.id)}
              onDecrease={handleDecrease(ticketType.id)}
            />
          ))}
        </View>

        <View style={styles.column}>
          <OrderSummary
            ticketQuantities={ticketQuantities}
            appliedPromo={appliedPromo}
            isExpandable={isMobile}
            onClearDiscount={handleClearDiscount}
          />
          <DiscountCodeInput ticketQuantities={ticketQuantities} />
        </View>
      </ScrollView>

      <View style={styles.buttonContainer}>
        <Button
          variant={ButtonVariants.PURPLE}
          onPress={handleNext}
          disabled={totalQuantity === 0}
        >
          Next
        </Button>
      </View>
    </View>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
    },
    scrollView: {
      flex: 1,
    },
    scrollContent: {
      padding: isMobile ? 12 : 20,
      flexDirection: isMobile ? 'column-reverse' : 'row',
      gap: 24,
      alignItems: isMobile ? 'center' : 'flex-start',
      justifyContent: 'center',
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 40,
    },
    emptyContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 40,
    },
    column: {
      flexGrow: 1,
      flexShrink: 1,
      maxWidth: 400,
      width: '100%',
      gap: 12,
    },
    buttonContainer: {
      padding: isMobile ? 12 : 16,
      gap: 16,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
    },
    errorText: {
      fontSize: 12,
      marginBottom: 8,
      textAlign: 'center',
    },
  });
